import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Card,
  CardContent,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import Grid from '@mui/material/Grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import AdUnitsIcon from '@mui/icons-material/AdUnits';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import Alert from '@mui/material/Alert';
import { useAuth } from '../contexts/AuthContext';
import { Ad, apiService } from '../services/api';
import ViewConnectionModal from './ViewConnectionModal';

interface AdField {
  name: string;
  value: string;
  type: 'input' | 'dropdown' | 'toggle';
  description?: string;
  options?: string[]; // dropdown options
}

interface AdOption {
  title: string;
  values: {
    name: string;
    value?: string | number;
  }[];
}

interface AdData {
  header: string;
  fields: AdField[];
  options: AdOption[];
  selectableOptionsQuantity: number;
  haveManageAds: boolean;
}

const AdDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { adName } = useParams();
  const { logout, clientSecret } = useAuth();

  const initialAd = location.state?.ad as Ad;
  const [ad, setAd] = useState<Ad | null>(initialAd);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [iframeContainerRef, setIframeContainerRef] = useState<HTMLDivElement | null>(null);
  const [iframeScale, setIframeScale] = useState<number>(0.5);
  const [iframeLoading, setIframeLoading] = useState<boolean>(true);
  const [viewConnectionModalOpen, setViewConnectionModalOpen] = useState(false);
  const pendingFieldsRef = useRef<Map<string, string>>(new Map());

  const adData = useMemo<AdData | null>(() => {
    if (!ad?.tx_ad_data) return null;
    try {
      return JSON.parse(ad.tx_ad_data);
    } catch (error) {
      console.error('Failed to parse ad data:', error);
      return null;
    }
  }, [ad?.tx_ad_data]);

  // Fetch ad data every second
  useEffect(() => {
    if (!adName || !clientSecret) return;

    const fetchAdData = async () => {
      try {
        const updatedAd = await apiService.getAdByName(adName, clientSecret, ad?.tx_match_id || undefined);
        setAd(updatedAd);
      } catch (error) {
        console.error('Failed to fetch ad data:', error);
      }
    };

    // Initial fetch
    fetchAdData();

    // Set up interval to fetch every second
    const interval = setInterval(fetchAdData, 1000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [adName, clientSecret]);

  // Sync field values from server, but skip fields whose latest local write
  // hasn't echoed back yet (server hasn't confirmed our PATCH).
  useEffect(() => {
    if (!adData?.fields) return;

    setFieldValues(prev => {
      const next: Record<string, string> = { ...prev };
      adData.fields.forEach(field => {
        const expected = pendingFieldsRef.current.get(field.name);
        if (expected !== undefined) {
          if (field.value === expected) {
            pendingFieldsRef.current.delete(field.name);
            next[field.name] = field.value;
          }
          return;
        }
        next[field.name] = field.value;
      });
      return next;
    });
  }, [adData]);

  // Calculate iframe scale based on container size
  useEffect(() => {
    if (!iframeContainerRef) return;

    const calculateScale = () => {
      const scaleX = iframeContainerRef.clientWidth / 1920;
      const scaleY = iframeContainerRef.clientHeight / 1080;
      const scale = Math.min(scaleX, scaleY);
      setIframeScale(scale);
    };

    // Initial calculation
    calculateScale();

    // Set up resize observer
    const resizeObserver = new ResizeObserver(calculateScale);
    resizeObserver.observe(iframeContainerRef);

    return () => {
      resizeObserver.disconnect();
    };
  }, [iframeContainerRef]);

  // 10-second fake loading timer - only runs once on mount
  useEffect(() => {
    setIframeLoading(true);
    const loadingTimer = setTimeout(() => {
      setIframeLoading(false);
    }, 15000);

    return () => {
      clearTimeout(loadingTimer);
    };
  }, []); // Empty dependency array - only runs once on mount

  const handleOptionClick = (optionTitle: string) => {
    if (!adData) return;
    
    setSelectedOptions(prev => {
      const isSelected = prev.includes(optionTitle);
      if (isSelected) {
        return prev.filter(title => title !== optionTitle);
      } else if (prev.length < adData.selectableOptionsQuantity) {
        return [...prev, optionTitle];
      }
      return prev;
    });
  };

  const handleFieldChange = async (fieldName: string, value: string) => {
    // Update local state
    setFieldValues(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Send PATCH request with only the changed field
    if (!adData || !ad || !clientSecret) return;

    // Find the field that changed
    const changedField = adData.fields.find(field => field.name === fieldName);
    if (!changedField) return;

    // Create the updated field object with the new value
    const updatedField = {
      ...changedField,
      value: value
    };

    pendingFieldsRef.current.set(fieldName, value);
    try {
      // Send PATCH request with only the changed field
      await apiService.updateAdDisplayText(ad.tx_ad_name, clientSecret, updatedField, ad.tx_match_id || undefined);
      console.log('Field updated successfully');
    } catch (error) {
      console.error('Failed to update field:', error);
      // Drop the lock so the field can resync to server state.
      if (pendingFieldsRef.current.get(fieldName) === value) {
        pendingFieldsRef.current.delete(fieldName);
      }
    }
  };

  const handleSubmitOptions = async () => {
    if (!adData || !ad || !clientSecret || selectedOptions.length !== adData.selectableOptionsQuantity) return;

    try {
      // Find the selected option objects based on their titles
      const selectedOptionObjects = selectedOptions.map(selectedTitle => 
        adData.options.find(option => option.title === selectedTitle)
      ).filter(Boolean); // Remove any undefined values

      let dataToSend;
      
      if (adData.selectableOptionsQuantity === 1) {
        // Send single option object
        dataToSend = selectedOptionObjects[0];
      } else {
        // Send array of selected options in order of selection
        dataToSend = selectedOptionObjects;
      }

      // Send PATCH request to update display_data
      await apiService.updateAdDisplayData(ad.tx_ad_name, clientSecret, dataToSend, ad.tx_match_id || undefined);
      console.log('Options submitted successfully');
      
      // Clear selected options after successful submission
      setSelectedOptions([]);
    } catch (error) {
      console.error('Failed to submit options:', error);
      // Optionally, you could show an error message to the user
    }
  };

  const renderField = (field: AdField, index: number) => {
    const value = fieldValues[field.name] ?? field.value;
    
    if (field.type === 'input') {
      return (
        <TextField
          key={field.name}
          fullWidth
          label={field.name}
          value={value}
          onChange={(e) => handleFieldChange(field.name, e.target.value)}
          helperText={field.description}
          variant="outlined"
          size="small"
        />
      );
    } else if (field.type === 'dropdown') {
      return (
        <FormControl key={field.name} fullWidth size="small">
          <InputLabel>{field.name}</InputLabel>
          <Select
            value={value}
            label={field.name}
            onChange={(e) => handleFieldChange(field.name, e.target.value as string)}
          >
            {field.options?.map((option, optIdx) => (
              <MenuItem key={optIdx} value={option}>
                {option}
              </MenuItem>
            )) || [
              <MenuItem key="default" value={field.value}>
                {field.value}
              </MenuItem>
            ]}
          </Select>
          {field.description && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {field.description}
            </Typography>
          )}
        </FormControl>
      );
    } else if (field.type === 'toggle') {
      const checked = value === 'true' || value === '1';
      return (
        <Box key={field.name}>
          <FormControlLabel
            control={
              <Switch
                checked={checked}
                onChange={(e) => handleFieldChange(field.name, e.target.checked ? 'true' : 'false')}
                disableRipple
                sx={{
                  width: 50,
                  height: 28,
                  padding: 0,
                  '& .MuiSwitch-switchBase': {
                    padding: '4px',
                    '&.Mui-checked': {
                      transform: 'translateX(28px)',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#44a838',
                        opacity: 1,
                        border: '1px solid #00BA00',
                      },
                    },
                  },
                  '& .MuiSwitch-thumb': {
                    width: 15,
                    height: 20,
                    borderRadius: '4px',
                    backgroundColor: '#ffffff',
                    boxShadow: 'none',
                  },
                  '& .MuiSwitch-track': {
                    borderRadius: '6px',
                    backgroundColor: '#e10000',
                    border: '1px solid #e10000',
                    opacity: 1,
                    transition: 'background-color 200ms, border-color 200ms',
                  },
                }}
              />
            }
            label={field.name}
            sx={{
              ml: 0,
              gap: 1.5,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.95rem',
                fontWeight: 500,
                color: 'text.primary',
              },
            }}
          />
          {field.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {field.description}
            </Typography>
          )}
        </Box>
      );
    }

    return null;
  };

  const renderOption = (option: AdOption) => {
    const isSelected = selectedOptions.includes(option.title);
    const canSelect = selectedOptions.length < (adData?.selectableOptionsQuantity || 0);
    const position = selectedOptions.indexOf(option.title) + 1;

    return (
      <Card
        key={option.title}
        sx={{
          cursor: 'pointer',
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'grey.300',
          backgroundColor: isSelected ? 'primary.50' : 'transparent',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: isSelected ? 'primary.100' : 'primary.50'
          },
          opacity: !canSelect && !isSelected ? 0.5 : 1,
          minHeight: 120,
          position: 'relative'
        }}
        onClick={() => (canSelect || isSelected) && handleOptionClick(option.title)}
      >
        {isSelected && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 28,
              height: 28,
              borderRadius: '50%',
              backgroundColor: '#00BA002E',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                color: '#00BA00',
                fontSize: '14px'
              }}
            >
              {position}
            </Typography>
          </Box>
        )}
        <CardContent sx={{ p: 2 }}>
          <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
            {option.title}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {option.values.map((value, idx) => (
              <Typography key={idx} variant="body2" color="text.secondary">
                {value.name}: {value.value !== undefined ? value.value : 'N/A'}
              </Typography>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const handleBack = () => {
    navigate('/ads');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleConnectionUpdated = () => {
    console.log('View connection data updated successfully');
  };

  const generateIframeUrl = () => {
    if (!ad || !clientSecret) return '';

    try {
      // Build the URL with the required format
      let url = 'https://iframe.extratime.cloud/extrastreamer-cloud/';

      // Add bundle name and version if available
      if (ad.bundle_ad?.bundle) {
        url += `${ad.bundle_ad.bundle.tx_name}/${ad.bundle_ad.bundle.tx_version}`;
      } else {
        url += 'default/v1';
      }

      // Add query parameters
      const params = new URLSearchParams();

      // Add match_id
      if (ad.tx_match_id) {
        params.append('matchId', ad.tx_match_id);
      }

      // Add ad name
      params.append('adName', ad.tx_ad_name);

      // Add client secret
      params.append('secret', clientSecret);

      // Add bundle fields from client_field_values
      if (ad.client_field_values) {
        // Handle new format: {"bundle_fields": {...}}
        if (ad.client_field_values.bundle_fields) {
          Object.entries(ad.client_field_values.bundle_fields).forEach(([key, value]) => {
            params.append(key, String(value));
          });
        }
        // Handle old format: direct bundle fields object
        else if (typeof ad.client_field_values === 'object' && !Array.isArray(ad.client_field_values)) {
          Object.entries(ad.client_field_values).forEach(([key, value]) => {
            params.append(key, String(value));
          });
        }
      }

      // Build final URL
      return url + '?' + params.toString();
    } catch (error) {
      console.error('Failed to generate iframe URL:', error);
      return '';
    }
  };

  if (!ad) {
    return (
      <>
        <AppBar position="static">
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleBack} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <AdUnitsIcon sx={{ mr: 2 }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Ad Details
            </Typography>
            <IconButton color="inherit" onClick={handleLogout}>
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Alert severity="error">
            Ad not found. Please go back and select an ad from the list.
          </Alert>
          <Button variant="contained" onClick={handleBack} sx={{ mt: 2 }}>
            Back to Ads List
          </Button>
        </Container>
      </>
    );
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <AdUnitsIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Ad Details - {adData?.header || 'Default'}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            {ad.tx_ad_name}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Ad ID: {ad.id}
              {ad.tx_match_id && (
                <>
                  {' • '}
                  Match ID: {ad.tx_match_id}
                </>
              )}
            </Typography>
            {ad.client_field_values && Object.keys(ad.client_field_values).length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {Object.entries(ad.client_field_values).map(([key, value]) => {
                    // Try to find the client-facing field name from bundle fields
                    const bundleField = ad.bundle_ad?.bundle?.bundle_fields?.find(field => field.tx_name === key);
                    const displayName = bundleField?.tx_client_field_name || key;

                    return (
                      <Box
                        key={key}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          bgcolor: 'primary.light',
                          color: 'primary.contrastText',
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          fontSize: '0.75rem'
                        }}
                      >
                        <strong>{displayName}:</strong>&nbsp;{String(value)}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
          
          {adData && (
            <>
              <Divider sx={{ my: 3 }} />
              
              {/* Fields Section */}
              {adData.fields && adData.fields.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                    Ad Fields
                  </Typography>
                  <Grid container rowSpacing={6} columnSpacing={3}>
                    {adData.fields.map((field, index) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }} key={field.name}>
                        {renderField(field, index)}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              
              <Divider sx={{ my: 3 }} />
              
              {/* Options Section */}
              {adData.options && adData.options.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                    Statistics Options
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                    Select up to {adData.selectableOptionsQuantity} options ({selectedOptions.length}/{adData.selectableOptionsQuantity} selected)
                  </Typography>
                  
                  {selectedOptions.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>Selected:</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {selectedOptions.map((option, index) => (
                          <Chip
                            key={option}
                            label={`${index + 1} - ${option}`}
                            onDelete={() => handleOptionClick(option)}
                            color="primary"
                            size="small"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  
                  <Grid container spacing={2}>
                    {adData.options.map((option) => (
                      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={option.title}>
                        {renderOption(option)}
                      </Grid>
                    ))}
                  </Grid>
                  
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Button 
                      variant="contained" 
                      color="primary"
                      disabled={selectedOptions.length !== adData.selectableOptionsQuantity}
                      onClick={handleSubmitOptions}
                      sx={{ px: 4, py: 1 }}
                    >
                      Submit Selected Options ({selectedOptions.length}/{adData.selectableOptionsQuantity})
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}

          {!adData && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No ad data available or invalid JSON format.
            </Alert>
          )}

          {/* Iframe Preview Section */}
          <Divider sx={{ my: 3 }} />
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h5" gutterBottom>
                  Ad Preview
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Live preview of your ad content
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<SettingsInputComponentIcon />}
                onClick={() => setViewConnectionModalOpen(true)}
              >
                Configure View Connection
              </Button>
            </Box>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                paddingBottom: '56.25%', // 16:9 aspect ratio (9/16 = 0.5625)
                height: 0,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'grey.50'
              }}
            >
              {generateIframeUrl() ? (
                <>
                  <Box
                    ref={setIframeContainerRef}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      overflow: 'hidden',
                      filter: iframeLoading ? 'blur(15px)' : 'none',
                      transition: 'filter 0.3s ease-in-out'
                    }}
                  >
                    <iframe
                      src={generateIframeUrl()}
                      style={{
                        width: '1920px',
                        height: '1080px',
                        border: 'none',
                        transform: `scale(${iframeScale})`,
                        transformOrigin: 'top left'
                      }}
                      title={`Ad Preview - ${ad.tx_ad_name}`}
                      allowFullScreen
                    />
                  </Box>
                  {iframeLoading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        zIndex: 2
                      }}
                    >
                      <CircularProgress size={60} thickness={4} />
                      <Typography
                        variant="body1"
                        sx={{ mt: 2, fontWeight: 'medium', color: 'white' }}
                      >
                        Loading ad preview...
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary'
                  }}
                >
                  <Typography>Unable to generate preview URL</Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-start' }}>
            <Button variant="contained" onClick={handleBack} startIcon={<ArrowBackIcon />}>
              Back to Ads List
            </Button>
          </Box>
        </Paper>
      </Container>

      <ViewConnectionModal
        open={viewConnectionModalOpen}
        onClose={() => setViewConnectionModalOpen(false)}
        adName={ad.tx_ad_name}
        matchId={ad.tx_match_id || undefined}
        clientSecret={clientSecret!}
        onConnectionUpdated={handleConnectionUpdated}
      />
    </>
  );
};

export default AdDetail;